import React, { useEffect, useState } from "react";
import axios from "axios";
import { Play, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

/* =====================================================
   Self-contained styles (no Tailwind dependency), so the
   layout always renders: 3 cards per row on desktop,
   2 on tablet, 1 on mobile.
===================================================== */
const styles = `
.hl-page {
  min-height: 100vh;
  background: #dce5e7;
  padding-top: 20px;
  font-family: inherit;
}
@media (min-width: 768px) {
  .hl-page { padding-top: 32px; }
}

/* ---------- Header ---------- */
.hl-header {
  position: relative;
  width: 100%;
  padding: 24px 64px;
  box-sizing: border-box;
  /* matches the "Latest News" heading theme */
  background: linear-gradient(90deg, #62a9f0 0%, #1f6bdc 100%);
  box-shadow: 0 8px 24px rgba(31, 107, 220, 0.25);
  text-align: center;
}
.hl-header h1 {
  margin: 0;
  color: #fff;
  font-weight: 700;
  font-size: 24px;
}
.hl-header p {
  margin: 8px 0 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}
@media (min-width: 768px) {
  .hl-header h1 { font-size: 36px; }
  .hl-header p { font-size: 16px; }
}

/* ---------- Back button ---------- */
.hl-back {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease, color 0.3s ease;
}
.hl-back:hover {
  background: #fff;
  color: #1f6bdc;
}
.hl-back:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}
@media (max-width: 599px) {
  .hl-header { padding: 16px 16px 20px; }
  .hl-back {
    position: static;
    transform: none;
    margin-bottom: 12px;
  }
}

/* ---------- Container + Grid ---------- */
.hl-container {
  width: 100%;
  max-width: 1450px;
  margin: 0 auto;
  padding: 40px 20px;
  box-sizing: border-box;
}
@media (min-width: 768px) {
  .hl-container { padding: 40px 32px; }
}

.hl-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}
@media (min-width: 700px) {
  .hl-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 1024px) {
  .hl-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

/* ---------- Card ---------- */
.hl-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #05161c;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(112, 197, 206, 0.18);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.25);
  transition: transform 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease;
}
.hl-card:hover,
.hl-card:focus-visible {
  transform: translateY(-8px);
  border-color: rgba(112, 197, 206, 0.6);
  box-shadow: 0 20px 50px rgba(112, 197, 206, 0.35);
  outline: none;
}

/* ---------- Thumbnail (16:9, identical on every card) ---------- */
.hl-thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #000;
  flex-shrink: 0;
}
.hl-thumb img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
/* blurred copy fills black bars of letterboxed thumbnails */
.hl-thumb .hl-bg {
  transform: scale(1.3);
  filter: blur(18px);
  opacity: 0.75;
}
/* slight zoom crops YouTube's black bars */
.hl-thumb .hl-main {
  transform: scale(1.12);
  transition: transform 0.7s ease;
}
.hl-card:hover .hl-main { transform: scale(1.22); }

.hl-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.15) 55%,
    rgba(0, 0, 0, 0.1) 100%
  );
}

.hl-badge {
  position: absolute;
  top: 14px;
  left: 14px;
  padding: 6px 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
}

.hl-play-wrap {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hl-play {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #70C5CE;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 35px rgba(112, 197, 206, 0.9);
  transform: scale(0.92);
  transition: transform 0.3s ease;
}
.hl-card:hover .hl-play { transform: scale(1.1); }

/* ---------- Card body ---------- */
.hl-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 18px;
  background: linear-gradient(180deg, #06202a 0%, #000 100%);
}
.hl-title {
  margin: 0;
  color: #fff;
  font-weight: 700;
  font-size: 17px;
  line-height: 1.45;
  min-height: calc(1.45em * 2);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.3s ease;
}
.hl-card:hover .hl-title { color: #70C5CE; }

.hl-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 14px;
}
.hl-arrow {
  color: #70C5CE;
  font-size: 18px;
  font-weight: 700;
  transition: transform 0.3s ease;
}
.hl-card:hover .hl-arrow { transform: translateX(4px); }

/* ---------- States ---------- */
.hl-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 96px 0;
}
.hl-state p {
  margin: 0;
  color: #374151;
  font-size: 18px;
  font-weight: 600;
}
.hl-empty {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
  padding: 48px 40px;
  text-align: center;
}

/* ---------- View more ---------- */
.hl-more-wrap {
  display: flex;
  justify-content: center;
  margin-top: 48px;
}
.hl-more {
  padding: 12px 40px;
  border: none;
  border-radius: 999px;
  background: #70C5CE;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  transition: background 0.3s ease, box-shadow 0.3s ease;
}
.hl-more:hover {
  background: #000;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.3);
}

@media (prefers-reduced-motion: reduce) {
  .hl-card, .hl-main, .hl-play, .hl-arrow { transition: none; }
}
`;

const Highlights = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [visible, setVisible] = useState(6);
  const [loading, setLoading] = useState(true);

  // Premier League ID
  const PREMIER_LEAGUE_ID = 39;

  // =========================
  // GET HIGHLIGHTS
  // =========================
  const getHighlights = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        "https://yofootballpro.econet.co.zw/api/highlights",
        {
          params: {
            league_id: PREMIER_LEAGUE_ID,
          },
        }
      );

      console.log("Highlights API Response:", res.data);

      if (res?.data?.success) {
        setVideos(res?.data?.response || []);
        setVisible(6);
      } else {
        setVideos([]);
      }
    } catch (error) {
      console.error(
        "Highlights API Error:",
        error?.response?.data || error?.message || error
      );

      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHighlights();
  }, []);

  // =========================
  // OPEN YOUTUBE VIDEO
  // =========================
  const openVideo = (id) => {
    if (!id) return;

    window.open(
      `https://www.youtube.com/watch?v=${id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // =========================
  // CLEAN TITLE (HTML entities from API)
  // =========================
  const cleanTitle = (title) =>
    (title || "")
      .replaceAll("&#39;", "'")
      .replaceAll("&amp;", "&")
      .replaceAll("&quot;", '"')
      .replaceAll("&lt;", "<")
      .replaceAll("&gt;", ">");

  return (
    <div className="hl-page">
      <style>{styles}</style>

      {/* HEADER */}
      <div className="hl-header">
        <button
          type="button"
          className="hl-back"
          onClick={() => navigate("/")}
          aria-label="Back to website"
        >
          <ArrowLeft size={18} />
          Back
        </button>
        <h1>Premier League EPL Highlights</h1>
        <p>Latest goals, skills & moments</p>
      </div>

      <div className="hl-container">
        {/* LOADING */}
        {loading && (
          <div className="hl-state">
            <p>Loading Highlights...</p>
          </div>
        )}

        {/* NO DATA */}
        {!loading && videos.length === 0 && (
          <div className="hl-state">
            <div className="hl-empty">
              <p>No Premier League highlights available.</p>
            </div>
          </div>
        )}

        {/* HIGHLIGHTS GRID */}
        {!loading && videos.length > 0 && (
          <>
            <div className="hl-grid">
              {videos.slice(0, visible).map((video) => (
                <div
                  key={video.id}
                  className="hl-card"
                  role="link"
                  tabIndex={0}
                  onClick={() => openVideo(video.youtube_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") openVideo(video.youtube_id);
                  }}
                >
                  {/* THUMBNAIL */}
                  <div className="hl-thumb">
                    {video.thumbnail && (
                      <img
                        className="hl-bg"
                        src={video.thumbnail}
                        alt=""
                        aria-hidden="true"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}

                    <img
                      className="hl-main"
                      src={video.thumbnail}
                      alt={cleanTitle(video.title) || "Premier League Highlight"}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />

                    <div className="hl-shade" />

                    <div className="hl-badge">Highlights</div>

                    <div className="hl-play-wrap">
                      <div className="hl-play">
                        <Play
                          fill="white"
                          color="white"
                          size={30}
                          style={{ marginLeft: 4 }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* BODY */}
                  <div className="hl-body">
                    <h2 className="hl-title" title={cleanTitle(video.title)}>
                      {cleanTitle(video.title)}
                    </h2>

                    <div className="hl-footer">
                      <span>Watch Now</span>
                      <span className="hl-arrow">▶</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* VIEW MORE */}
            {visible < videos.length && (
              <div className="hl-more-wrap">
                <button
                  className="hl-more"
                  onClick={() => setVisible((prev) => prev + 6)}
                >
                  View More
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Highlights;